import { Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLineUsers } from '@/hooks/useLineData';
import { formatDateTime } from '@/lib/firestoreTime';

import { SessionsView } from '@/pages/line/sessions/SessionsPage';

type TabKey = 'users' | 'sessions';

export function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial: TabKey = searchParams.get('tab') === 'sessions' ? 'sessions' : 'users';
  const [tab, setTab] = useState<TabKey>(initial);
  const selectedUid = searchParams.get('uid');

  // タブ変更を URL クエリにも反映
  useEffect(() => {
    const cur = searchParams.get('tab');
    const next = tab === 'sessions' ? 'sessions' : null;
    if ((cur ?? null) !== next) {
      const params = new URLSearchParams(searchParams);
      if (next) params.set('tab', next);
      else params.delete('tab');
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleSelectUser = (uid: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('uid', uid);
    params.set('tab', 'sessions');
    setSearchParams(params, { replace: false });
    setTab('sessions');
  };

  const handleSelectUidInSessions = (uid: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('uid', uid);
    setSearchParams(params, { replace: false });
  };

  return (
    <Screen
      title="ユーザー一覧"
      description="LINE 友だち登録されているユーザーの一覧と会話履歴を閲覧できます"
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="users">ユーザー</TabsTrigger>
          <TabsTrigger value="sessions">会話履歴</TabsTrigger>
        </TabsList>
        <TabsContent value="users">
          <UsersListView onSelectUser={handleSelectUser} />
        </TabsContent>
        <TabsContent value="sessions">
          <SessionsView
            selectedUid={selectedUid}
            onSelectUid={handleSelectUidInSessions}
          />
        </TabsContent>
      </Tabs>
    </Screen>
  );
}

function UsersListView({ onSelectUser }: { onSelectUser: (uid: string) => void }) {
  const { data, isLoading } = useLineUsers();

  if (isLoading) return <Spinner />;
  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="まだユーザーがいません"
        description="LINE Bot にメッセージを送信するとここに表示されます"
      />
    );
  }

  return (
    <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
      {data.map((u) => (
        <button
          type="button"
          key={u.id}
          onClick={() => onSelectUser(u.id)}
          className="w-full flex items-center gap-4 p-4 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors text-left text-[var(--text-primary)]"
        >
          <div className="size-9 rounded-full bg-[var(--bg-hover)] flex items-center justify-center flex-shrink-0 text-sm font-semibold">
            {(u.displayName ?? '?').charAt(0) || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-medium text-[13px] truncate">
                {u.displayName || '(名無し)'}
              </span>
              {u.conversationActive && <Badge variant="approved">会話中</Badge>}
              {u.currentInquiryType && (
                <Badge variant="accent">{u.currentInquiryType}</Badge>
              )}
            </div>
            <div className="text-xs text-[var(--text-muted)] flex items-center gap-3">
              <code className="font-mono text-[10px]">{u.id}</code>
              <span>最終: {formatDateTime(u.lastMessageAt)}</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
